import React, { useMemo } from 'react';
import {
  AlertTriangle,
  LayoutList,
  BarChart2,
  Plus,
  Layers,
  X,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import PhaseGanttChart from '@/components/PhaseGanttChart';
import BomSection from '@/components/project/BomSection';

const PhasesTab = ({
  project,
  phases,
  sortedPhases,
  orders,
  phaseBudgetExceeds,
  totalPhaseBudget,
  expandedPhase,
  setExpandedPhase,
  phasesView,
  handlePhasesViewChange,
  isMobile,
  isTinyScreen,
  setShowNewPhase,
  handleUpdatePhaseStatus,
  handleUpdatePhaseDates,
  setConfirmDelete,
  formatCurrency,
  formatDate,
  getPhaseStatusClass,
  getPhaseTimelineColor,
  bomFulfillmentMap,
  refreshBomMap,
}) => {
  // Memoize delivered orders grouped by phase (filtered twice per phase card originally)
  const deliveredOrdersByPhase = useMemo(() => {
    const map = {};
    for (const o of orders) {
      if (o.status === 'Delivered' && o.phase_id) {
        (map[o.phase_id] ||= []).push(o);
      }
    }
    return map;
  }, [orders]);

  return (
    <>
      {/* Phase Budget Warning */}
      {phaseBudgetExceeds && (
        <div className="bg-yellow-50 border-2 border-yellow-500 p-4 mb-4 flex items-start gap-3" data-testid="phase-budget-warning">
          <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
          <div>
            <p className="font-bold text-yellow-800">Phase budgets exceed project budget</p>
            <p className="text-sm text-yellow-700">
              Total phase budgets: {formatCurrency(totalPhaseBudget)} / Project budget: {formatCurrency(project.total_budget)}
            </p>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-4 gap-2 flex-wrap">
        <h2 className="font-heading text-lg font-bold uppercase tracking-wide">Project Phases</h2>
        <div className="flex items-center gap-2">
          {!isTinyScreen && phases.length > 0 && (
            <div className="inline-flex border-2 border-black bg-white" data-testid="phases-view-toggle">
              <button
                onClick={() => handlePhasesViewChange('list')}
                data-testid="phases-view-list-btn"
                className={`h-12 px-3 flex items-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider transition-colors ${
                  phasesView === 'list' ? 'bg-primary text-zinc-900' : 'bg-white text-zinc-600 hover:bg-zinc-50'
                }`}
                title="List view"
              >
                <LayoutList className="w-4 h-4" />
                <span className="hidden sm:inline">List</span>
              </button>
              <button
                onClick={() => handlePhasesViewChange('chart')}
                data-testid="phases-view-chart-btn"
                className={`h-12 px-3 flex items-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider border-l-2 border-black transition-colors ${
                  phasesView === 'chart' ? 'bg-primary text-zinc-900' : 'bg-white text-zinc-600 hover:bg-zinc-50'
                }`}
                title="Chart view"
              >
                <BarChart2 className="w-4 h-4" />
                <span className="hidden sm:inline">Chart</span>
              </button>
            </div>
          )}
          <button
            onClick={() => setShowNewPhase(true)}
            data-testid="new-phase-btn"
            className="h-12 px-4 bg-primary text-zinc-900 font-bold uppercase tracking-wide border border-zinc-200  hover:bg-primary-hover active:translate-y-1 active: transition-all flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Phase
          </button>
        </div>
      </div>

      {phases.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-8 text-center ">
          <Layers className="w-16 h-16 mx-auto mb-4 text-zinc-500" />
          <p className="text-lg font-bold mb-2">No phases yet</p>
          <p className="text-zinc-500">Add phases to track project progress</p>
        </div>
      ) : (phasesView === 'chart' && !isTinyScreen) ? (
        /* ===== GANTT CHART VIEW ===== */
        <div className="space-y-4" data-testid="phases-chart-view">
          <PhaseGanttChart
            project={project}
            phases={phases}
            sortedPhases={sortedPhases}
            expandedPhase={expandedPhase}
            setExpandedPhase={setExpandedPhase}
            isMobile={isMobile}
            formatCurrency={formatCurrency}
            onUpdateDates={handleUpdatePhaseDates}
            bomFulfillmentMap={bomFulfillmentMap}
          />

          {/* Expanded phase detail card (below chart) */}
          {expandedPhase && (() => {
            const phase = phases.find(p => p.phase_id === expandedPhase);
            if (!phase) return null;
            const phaseSpentPercent = phase.budget > 0 ? (phase.spent_amount / phase.budget) * 100 : 0;
            return (
              <div
                className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
                data-testid={`phase-detail-${phase.phase_id}`}
              >
                <div className="p-4 border-b-2 border-black flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                      <h3 className="font-heading font-bold text-lg uppercase tracking-wide">{phase.name}</h3>
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase border border-black ${getPhaseStatusClass(phase.status)}`}>
                        {phase.status}
                      </span>
                    </div>
                    {(phase.start_date || phase.end_date) && (
                      <p className="text-sm text-zinc-500 font-mono">
                        {formatDate(phase.start_date)} - {formatDate(phase.end_date) || 'TBD'}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-heading text-lg font-bold uppercase tracking-wide">{formatCurrency(phase.budget)}</p>
                      <p className="text-xs text-zinc-500">{formatCurrency(phase.spent_amount)} spent</p>
                    </div>
                    <button
                      onClick={() => setExpandedPhase(null)}
                      data-testid={`phase-detail-close-${phase.phase_id}`}
                      className="h-9 w-9 flex items-center justify-center border-2 border-black bg-white hover:bg-zinc-100"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="p-4 bg-zinc-50 space-y-4">
                  <div className="h-2 bg-zinc-100 border border-black overflow-hidden">
                    <div
                      className={`h-full ${
                        phaseSpentPercent >= 90 ? 'bg-red-500' :
                        phaseSpentPercent >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(phaseSpentPercent, 100)}%` }}
                    />
                  </div>

                  {phase.description && (
                    <p className="text-zinc-600">{phase.description}</p>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <select
                      value={phase.status}
                      onChange={(e) => handleUpdatePhaseStatus(phase.phase_id, e.target.value)}
                      data-testid={`phase-status-select-${phase.phase_id}`}
                      className={`h-10 px-3 text-xs font-bold uppercase border border-zinc-200 ${getPhaseStatusClass(phase.status)}`}
                    >
                      <option value="Not Started">Not Started</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Complete">Complete</option>
                      <option value="Delayed">Delayed</option>
                    </select>
                    <button
                      onClick={() => setConfirmDelete({ type: 'phase', id: phase.phase_id, name: phase.name })}
                      data-testid={`phase-delete-btn-${phase.phase_id}`}
                      className="h-10 px-4 flex items-center gap-2 border border-zinc-200 bg-white hover:bg-destructive hover:text-white transition-colors text-xs font-bold uppercase"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>

                  <div className="border-t border-gray-300 pt-3">
                    <p className="text-xs font-bold uppercase text-zinc-500 mb-2">Delivered orders in this phase:</p>
                    {(deliveredOrdersByPhase[phase.phase_id] || []).length === 0 ? (
                      <p className="text-sm text-zinc-500">No delivered orders linked to this phase</p>
                    ) : (
                      <div className="space-y-1">
                        {(deliveredOrdersByPhase[phase.phase_id] || []).map(order => (
                          <div key={order.order_id} className="text-sm flex justify-between">
                            <span>{order.vendor_name} - {order.po_number || 'No PO'}</span>
                            <span className="font-bold">{formatCurrency(order.total)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <BomSection phaseId={phase.phase_id} onChange={refreshBomMap} />
                </div>
              </div>
            );
          })()}
        </div>
      ) : (
        /* Vertical Timeline */
        <div className="relative" data-testid="phases-list-view">
          {sortedPhases.map((phase, index) => {
            const isExpanded = expandedPhase === phase.phase_id;
            const phaseSpentPercent = phase.budget > 0 ? (phase.spent_amount / phase.budget) * 100 : 0;

            return (
              <div key={phase.phase_id} className="relative pl-8 pb-6 last:pb-0" data-testid={`phase-card-${phase.phase_id}`}>
                {index < sortedPhases.length - 1 && (
                  <div className="absolute left-3 top-6 w-0.5 h-full bg-gray-300" />
                )}

                <div className={`absolute left-0 top-1 w-6 h-6 rounded-full border border-zinc-200 flex items-center justify-center ${getPhaseTimelineColor(phase.status)}`}>
                  {phase.status === 'Complete' && <CheckCircle className="w-4 h-4 text-white" />}
                  {phase.status === 'In Progress' && <Clock className="w-4 h-4 text-white" />}
                  {phase.status === 'Delayed' && <AlertCircle className="w-4 h-4 text-white" />}
                </div>

                <div className="bg-white border border-zinc-200 ">
                  <button
                    onClick={() => setExpandedPhase(isExpanded ? null : phase.phase_id)}
                    className="w-full p-4 text-left"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap mb-1">
                          <h3 className="font-bold text-lg">{phase.name}</h3>
                          <span className={`px-2 py-0.5 text-xs font-bold uppercase border border-black ${getPhaseStatusClass(phase.status)}`}>
                            {phase.status}
                          </span>
                        </div>
                        {(phase.start_date || phase.end_date) && (
                          <p className="text-sm text-zinc-500">
                            {formatDate(phase.start_date)} - {formatDate(phase.end_date) || 'TBD'}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-heading text-lg font-bold uppercase tracking-wide">{formatCurrency(phase.budget)}</p>
                          <p className="text-xs text-zinc-500">{formatCurrency(phase.spent_amount)} spent</p>
                        </div>
                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>

                    <div className="h-2 bg-zinc-100 border border-black mt-3 overflow-hidden">
                      <div
                        className={`h-full ${
                          phaseSpentPercent >= 90 ? 'bg-red-500' :
                          phaseSpentPercent >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(phaseSpentPercent, 100)}%` }}
                      />
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t-2 border-black p-4 bg-zinc-50">
                      {phase.description && (
                        <p className="text-zinc-500 mb-4">{phase.description}</p>
                      )}

                      <div className="flex flex-wrap gap-2 mb-4">
                        <select
                          value={phase.status}
                          onChange={(e) => handleUpdatePhaseStatus(phase.phase_id, e.target.value)}
                          className={`h-10 px-3 text-xs font-bold uppercase border border-zinc-200 ${getPhaseStatusClass(phase.status)}`}
                        >
                          <option value="Not Started">Not Started</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Complete">Complete</option>
                          <option value="Delayed">Delayed</option>
                        </select>
                        <button
                          onClick={() => setConfirmDelete({ type: 'phase', id: phase.phase_id, name: phase.name })}
                          className="h-10 px-4 flex items-center gap-2 border border-zinc-200 bg-white hover:bg-destructive hover:text-white transition-colors text-xs font-bold uppercase"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>

                      <div className="border-t border-gray-300 pt-3">
                        <p className="text-xs font-bold uppercase text-zinc-500 mb-2">Delivered orders in this phase:</p>
                        {(deliveredOrdersByPhase[phase.phase_id] || []).length === 0 ? (
                          <p className="text-sm text-zinc-500">No delivered orders linked to this phase</p>
                        ) : (
                          <div className="space-y-1">
                            {(deliveredOrdersByPhase[phase.phase_id] || []).map(order => (
                              <div key={order.order_id} className="text-sm flex justify-between">
                                <span>{order.vendor_name} - {order.po_number || 'No PO'}</span>
                                <span className="font-bold">{formatCurrency(order.total)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="mt-4">
                        <BomSection phaseId={phase.phase_id} onChange={refreshBomMap} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default PhasesTab;
